import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RestoreIcon from '@mui/icons-material/Restore';

// Types
import { Setting } from '../../types/Settings';

// A merged row: a global (overridable) setting plus its optional company override.
export interface MergedSettingRow {
  global: Setting;
  override?: Setting;
}

const renderValue = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

export const makeCompanySettingColumns = (onEdit: (row: MergedSettingRow) => void, onRevert: (override: Setting) => void) => {
  const cols = [
    {
      id: 'actions',
      header: '',
      size: 90,
      cell: ({ row }: { row: { original: MergedSettingRow } }) => {
        const { global, override } = row.original;
        return (
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <Tooltip title='Edit override'>
              <IconButton size='small' onClick={() => onEdit({ global, override })}>
                <EditOutlinedIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            {override && (
              <Tooltip title='Revert to global'>
                <IconButton size='small' color='warning' onClick={() => onRevert(override)}>
                  <RestoreIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      header: 'Setting',
      accessorFn: (row: MergedSettingRow) => row.global.name,
      id: 'name',
      size: 220,
      cell: ({ row }: { row: { original: MergedSettingRow } }) => <span style={{ fontWeight: 500 }}>{row.original.global.name}</span>,
    },
    {
      header: 'Scope',
      accessorFn: (row: MergedSettingRow) => row.global.scope,
      id: 'scope',
      size: 140,
      cell: ({ row }: { row: { original: MergedSettingRow } }) => <span style={{ color: '#6B7280' }}>{row.original.global.scope}</span>,
    },
    {
      header: 'Global Value',
      accessorFn: (row: MergedSettingRow) => renderValue(row.global.value),
      id: 'globalValue',
      size: 240,
      cell: ({ row }: { row: { original: MergedSettingRow } }) => <span>{renderValue(row.original.global.value)}</span>,
    },
    {
      header: 'Company Override',
      accessorFn: (row: MergedSettingRow) => (row.override ? renderValue(row.override.value) : ''),
      id: 'overrideValue',
      size: 240,
      cell: ({ row }: { row: { original: MergedSettingRow } }) => {
        const { override } = row.original;
        if (!override) {
          return <span style={{ color: '#9CA3AF' }}>—</span>;
        }
        return <span style={{ color: '#2563EB', fontWeight: 600 }}>{renderValue(override.value)}</span>;
      },
    },
  ];

  return cols;
};
